import {Injectable, signal} from "@angular/core"
import {MaterialGraph} from "@cm/material-nodes"

/**
 * Service that bridges MaterialGraph's imperative change notifications with Angular's reactive system.
 *
 * MaterialGraph and MaterialNode are plain JS classes, not signals. When their internal state changes,
 * Angular's change detection doesn't see it (same object reference = no change). This service provides
 * a signal that gets bumped whenever the graph changes, allowing computed signals to react.
 *
 * Usage in components:
 * ```typescript
 * readonly graphChange = inject(MaterialGraphChangeService)
 *
 * readonly $someValue = computed(() => {
 *     this.graphChange.$graphVersion() // Trigger recomputation on graph changes
 *     return this.node.parameters.settings.someValue
 * })
 * ```
 */
@Injectable()
export class MaterialGraphChangeService {
    readonly $graphVersion = signal(0)

    connectToGraph(graph: MaterialGraph) {
        graph.onHashInvalidated = () => this.$graphVersion.update((c) => c + 1)
    }
}
